import { Button } from "@web/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@web/components/ui/card";
import { Input } from "@web/components/ui/input";
import { Label } from "@web/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@web/components/ui/select";
import { Textarea } from "@web/components/ui/textarea";
import { useLanguage } from "@web/lib/contexts/language-context";
import { reportSeal } from "@web/lib/requests/report";
import { AlertTriangle, Send, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

export function DenunciarPage() {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    informerName: "",
    informerEmail: "",
    informerPhone: "",
    serialNumber: "",
    // productId: "",
    productBrand: "",
    productName: "",
    shopName: "",
    reportReason: "",
    reportDescription: "",
  });

  const [images, setImages] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setImages((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (images.length === 0) {
      alert(t.denouncePage.imagesRequired ?? "Please add at least one image.");
      return;
    }

    setIsSubmitting(true);
    try {
      await reportSeal({
        informerName: form.informerName,
        informerEmail: form.informerEmail,
        informerPhone: form.informerPhone || undefined,
        serialNumber: form.serialNumber,
        // productId: form.productId || undefined,
        productBrand: form.productBrand,
        productName: form.productName,
        shopName: form.shopName,
        reportReason: form.reportReason,
        reportDescription: form.reportDescription,
        images: images.length === 1 ? images[0] : images,
      });
      toast.success("Denúncia enviada com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Ocorreu um erro ao enviar a denúncia");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full flex flex-1 items-center justify-center">
      <main className="flex flex-1 flex-col items-center p-4 md:p-8">
        <Card className="w-full max-w-3xl">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div>
                <CardTitle className="text-xl">
                  {t.denouncePage.title}
                </CardTitle>
                <CardDescription className="mt-1">
                  {t.denouncePage.subtitle}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="flex flex-col gap-4">
                <h3 className="font-semibold text-foreground">
                  {t.denouncePage.reporterSectionTitle}
                </h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="name">{t.denouncePage.nameLabel}</Label>
                    <Input
                      id="name"
                      placeholder={t.denouncePage.namePlaceholder}
                      value={form.informerName}
                      onChange={(e) =>
                        handleChange("informerName", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="email">{t.denouncePage.emailLabel}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t.denouncePage.emailPlaceholder}
                      value={form.informerEmail}
                      onChange={(e) =>
                        handleChange("informerEmail", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="phone">{t.denouncePage.phoneLabel}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={t.denouncePage.phonePlaceholder}
                    value={form.informerPhone}
                    onChange={(e) =>
                      handleChange("informerPhone", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <h3 className="font-semibold text-foreground">
                  {t.denouncePage.sealSectionTitle}
                </h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="seal-code">
                      {t.denouncePage.sealCodeLabel}
                    </Label>
                    <Input
                      id="seal-code"
                      placeholder={t.denouncePage.sealCodePlaceholder}
                      value={form.serialNumber}
                      onChange={(e) =>
                        handleChange("serialNumber", e.target.value)
                      }
                      required
                    />
                  </div>

                  {/*
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="product-id">
                      {t.denouncePage.sealTypeLabel}
                    </Label>
                    <Input
                      id="product-id"
                      placeholder={t.denouncePage.sealTypePlaceholder}
                      value={form.productId}
                      onChange={(e) =>
                        handleChange("productId", e.target.value)
                      }
                    />
                  </div>
                  */}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="brand">{t.denouncePage.brandLabel}</Label>
                    <Input
                      id="brand"
                      placeholder={t.denouncePage.brandPlaceholder}
                      value={form.productBrand}
                      onChange={(e) =>
                        handleChange("productBrand", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="product">
                      {t.denouncePage.productLabel}
                    </Label>
                    <Input
                      id="product"
                      placeholder={t.denouncePage.productPlaceholder}
                      value={form.productName}
                      onChange={(e) =>
                        handleChange("productName", e.target.value)
                      }
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="location">
                    {t.denouncePage.locationLabel}
                  </Label>
                  <Input
                    id="location"
                    placeholder={t.denouncePage.locationPlaceholder}
                    value={form.shopName}
                    onChange={(e) => handleChange("shopName", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <h3 className="font-semibold text-foreground">
                  {t.denouncePage.detailsSectionTitle}
                </h3>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="reason">{t.denouncePage.reasonLabel}</Label>
                  <Select
                    required
                    value={form.reportReason}
                    onValueChange={(value) =>
                      handleChange("reportReason", value)
                    }
                  >
                    <SelectTrigger id="reason">
                      <SelectValue
                        placeholder={t.denouncePage.reasonPlaceholder}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fake-seal">
                        {t.denouncePage.reasonOptions.fakeSeal}
                      </SelectItem>
                      <SelectItem value="invalid-code">
                        {t.denouncePage.reasonOptions.invalidCode}
                      </SelectItem>
                      <SelectItem value="duplicate">
                        {t.denouncePage.reasonOptions.duplicate}
                      </SelectItem>
                      <SelectItem value="damaged">
                        {t.denouncePage.reasonOptions.damaged}
                      </SelectItem>
                      <SelectItem value="suspicious">
                        {t.denouncePage.reasonOptions.suspicious}
                      </SelectItem>
                      <SelectItem value="other">
                        {t.denouncePage.reasonOptions.other}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="description">
                    {t.denouncePage.descriptionLabel}
                  </Label>
                  <Textarea
                    id="description"
                    placeholder={t.denouncePage.descriptionPlaceholder}
                    className="min-h-32 resize-none"
                    value={form.reportDescription}
                    onChange={(e) =>
                      handleChange("reportDescription", e.target.value)
                    }
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <h3 className="font-semibold text-foreground">
                  {t.denouncePage.imagesSectionTitle ?? "Images"}
                </h3>

                {/** biome-ignore lint/a11y/noStaticElementInteractions: <explanation> */}
                <div
                  className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 px-6 py-8 text-center cursor-pointer hover:border-muted-foreground/60 hover:bg-muted/30 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      fileInputRef.current?.click();
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files).filter((f) =>
                      f.type.startsWith("image/"),
                    );
                    setImages((prev) => [...prev, ...files]);
                  }}
                >
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">
                    {t.denouncePage.imagesUploadLabel ??
                      "Click or drag images here"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t.denouncePage.imagesUploadHint ??
                      "PNG, JPG, WEBP up to 10MB each"}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </div>

                {images.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                    {images.map((file, index) => (
                      <div key={index} className="relative group aspect-square">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="h-full w-full rounded-md object-cover border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity shadow"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <p className="mt-1 truncate text-xs text-muted-foreground">
                          {file.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button
                  type="submit"
                  className="min-w-40"
                  disabled={isSubmitting}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {isSubmitting
                    ? (t.denouncePage.submittingText ?? "Sending...")
                    : t.denouncePage.submitButtonText}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
